import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * FaqAccordion — single-open accordion for service-page FAQs.
 * Renders structured-data friendly content (each Q is a real <button> with aria-expanded).
 */
const FaqAccordion = ({ items = [], eyebrow = "Frequently asked", title = "Common questions." }) => {
  const [open, setOpen] = useState(0);
  if (!items.length) return null;

  return (
    <section className="bg-white ek-section" data-testid="faq-section">
      <div className="ek-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="ek-eyebrow">{eyebrow}</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black">{title}</h2>
            <p className="ek-body mt-6 max-w-md">
              Quick answers to the questions buyers most often ask before a discovery call.
              Don't see your question? <a href="/contact" className="text-[#7737F6] underline underline-offset-4">Ask us directly</a>.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
              {items.map((f, i) => {
                const isOpen = open === i;
                return (
                  <li key={f.q || i} data-testid={`faq-item-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-body-${i}`}
                      className="w-full text-left py-5 flex items-start gap-4 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7737F6] focus-visible:ring-offset-2"
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] uppercase shrink-0 w-8 mt-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="ek-heading text-base md:text-lg text-black flex-1 leading-snug">
                        {f.q}
                      </span>
                      <ChevronDown
                        size={20}
                        strokeWidth={1.5}
                        className={`shrink-0 text-zinc-500 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#7737F6]" : ""}`}
                      />
                    </button>
                    <div
                      id={`faq-body-${i}`}
                      role="region"
                      hidden={!isOpen}
                      className="pb-6 pl-12 pr-2"
                      data-testid={`faq-body-${i}`}
                    >
                      <p className="ek-body text-[14.5px] leading-relaxed">{f.a}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqAccordion;
